import { ReactComponent as ChevronBreadCrumbIcon } from 'assett/icons/chevron_breadcrumb_icon.svg';
import { ReactComponent as ChevronBreadCrumbIconTablet } from 'assett/icons/chevron_breadcrumb_icon_tablet.svg';
import useDevice from 'hooks/DesktopDevice.hook';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'store/store';
import BreadcrumbsMui from 'styles/breadcrumbs/Breadcrumbs';
import Button from 'styles/button/Button';
import Typography from 'styles/typography/Typograpy';
import { PATHNAMES } from 'utils/constants';
import { useTranslation } from 'utils/translations';

interface Props {
	claimUuid: string;
	externalClaimId: string;
}

const Breadcrumbs = (props: Props) => {
	const location = useLocation();
	const navigate = useNavigate();
	const pathnames = location.pathname.split('/');
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { isDesktop } = useDevice();
	const { member } = useSelector((store: RootState) => store.member);

	const { id } = useParams();

	const dateOfService = new URLSearchParams(location.search).get(
		'dateOfService',
	);

	const formatBreadcrumb = (route: string, index: number) => {
		if (pathnames[1] === PATHNAMES.CHECK_AND_SUBMIT && index === 2) {
			return member?.memberName;
		}

		// if we are inside the claims and the view is the single claim
		if (
			[PATHNAMES.VIEW_CLAIMS, PATHNAMES.CLAIMS_NOTIFICATIONS].includes(
				pathnames[1],
			) &&
			index > 1 &&
			index === pathnames.length - 1
		) {
			return `${tClientLabels('claim.breadcrumb')} #${props.externalClaimId}`;
		}

		if (
			pathnames[3] === PATHNAMES.NEW_CLAIM &&
			index > 1 &&
			index === pathnames.length - 1
		) {
			return !props.externalClaimId
				? tClientLabels('new-claim.pageTitleNew')
				: `${tClientLabels('new-claim.pageTitleOld')} #${props.externalClaimId}`;
		}

		if (pathnames[1] === PATHNAMES.VIEW_PAYMENT_HISTORY) {
			// if we are inside the payments list
			// we are inside the assessment fee invoice view
			if (index === 4) {
				return tClientLabels('assessment-fee-invoice.pageTitle');
			}

			if (index === 2) {
				return `${tClientLabels('payment.details.ticketTitle')}${
					route.split('|')[0]
				}`;
			}

			// we are inside a payment detail
			if (index > 2 && !parseInt(pathnames[index])) {
				return route;
			}

			// we are inside the specific payment
			if (index > 1 && parseInt(pathnames[2])) {
				return `${tClientLabels('payment.breadcrumb')} #${route}`;
			}

			// we are inside the list or the details
			return route;
		}

		// none of the other cases
		return route;
	};

	return (
		<BreadcrumbsMui
			sx={{ cursor: 'pointer', mt: { sm: 1.25, lg: 2.5 } }}
			separator={
				isDesktop ? <ChevronBreadCrumbIcon /> : <ChevronBreadCrumbIconTablet />
			}
		>
			{pathnames
				.map(e => {
					if (decodeURI(e) === props.claimUuid) {
						return props.claimUuid;
					}
					return decodeURI(e) === id ? id : tClientLabels(`${e}.pageTitle`);
				})
				.map((route, index) => {
					if (
						index === 3 &&
						route === tClientLabels('assessment-fee-invoice.pageTitle')
					) {
						return null;
					}
					if (index >= 2 && pathnames[1] === PATHNAMES.DOCUMENTATION) {
						return null;
					}
					if (index === 0) {
						return (
							<Button
								variant="link"
								sx={{ minWidth: 0 }}
								onClick={() => navigate('/')}
								key={index}
							>
								<Typography variant="value3" sx={{ color: 'grey2.main' }}>
									{'Home'}
								</Typography>
							</Button>
						);
					}
					return (
						<Button
							variant="link"
							sx={{ minWidth: 0 }}
							onClick={() => {
								const path = [];

								for (let i = 1; i <= index; i++) {
									path.push(`/${pathnames[i]}`);
								}

								navigate(
									`${path.join('')}${
										dateOfService && index > 1 ? `?dateOfService=${dateOfService}` : ''
									}`,
								);
							}}
							key={index}
						>
							<Typography
								variant="value3"
								sx={{
									color: index === pathnames.length - 1 ? 'primary.main' : 'grey2.main',
								}}
							>
								{formatBreadcrumb(route, index)}
							</Typography>
						</Button>
					);
				})}
		</BreadcrumbsMui>
	);
};

export default Breadcrumbs;
